import { useMemo } from "react";
import { useGetList } from "ra-core";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { atalhosDeEquipe, rotuloDaSelecao, type UsuarioFiltro } from "../filtroVendedores";

/**
 * Seleção de vários vendedores em Vendas, só para super admin (o gerente e o
 * vendedor continuam com o seletor de um só). Mesmo desenho do "Ver como" da
 * Dashboard: atalhos de equipe em cima, caixinhas embaixo.
 */
export const FiltroVendedoresMulti = ({
  selecionados = [],
  aoMudar,
  className,
}: {
  selecionados: number[];
  aoMudar: (ids: number[]) => void;
  className?: string;
}) => {
  const { data } = useGetList<UsuarioFiltro>("users", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
    filter: { "disabled@neq": true },
  });
  const usuarios = useMemo(
    () => (data ?? []).filter((u) => u.name).sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [data],
  );
  const atalhos = useMemo(() => atalhosDeEquipe(usuarios), [usuarios]);
  const marcados = new Set(selecionados);

  const alternar = (id: number) =>
    aoMudar(marcados.has(id) ? selecionados.filter((x) => x !== id) : [...selecionados, id]);
  // Somar equipe à seleção: misturar equipe e avulsos é permitido; repetido conta uma vez.
  const somar = (ids: number[]) => aoMudar([...new Set([...selecionados, ...ids])]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("justify-between bg-card font-normal", className)}>
          <span className="truncate">{rotuloDaSelecao(selecionados, usuarios)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => aoMudar(usuarios.map((u) => Number(u.id)))}
          >
            Marcar todos
          </button>
          <button
            type="button"
            className="text-xs text-primary hover:underline disabled:opacity-40"
            disabled={selecionados.length === 0}
            onClick={() => aoMudar([])}
          >
            Limpar seleção
          </button>
        </div>

        {atalhos.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-b px-3 py-2">
            {atalhos.map((a) => (
              <button
                key={a.gestorId}
                type="button"
                className="rounded bg-muted px-2 py-1 text-xs font-medium hover:bg-muted/70"
                onClick={() => somar(a.ids)}
              >
                {a.rotulo}
              </button>
            ))}
          </div>
        )}

        <div className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable="true" aria-label="Vendedores">
          {usuarios.map((u) => {
            const id = Number(u.id);
            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={marcados.has(id)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted/50"
                onClick={() => alternar(id)}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border">
                  {marcados.has(id) && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{u.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
